import React, { useEffect, useState } from "react";
import { Box, Card, Typography, useMediaQuery, useTheme } from "@mui/material";
import MaterialTable from "../../../Components/MaterialTable";
import { IColumn } from "../../../Components/MaterialTable/MaterialTable";
import { formatCurrency } from "../../../utils/formatCurrency";
import { formatDate } from "../../../utils/formatDate";
import TransferBreakdownModal from "./TransferBreakdownModal";
import AffiliateTransfersCardsList from "./AffiliateTransfersCardsList";
import { AffiliateTransfer, IPayOutType, PayoutMethod } from "../../../store/services/affiliate/dto/affiliate.dto";
import { useLazyGetAffiliateTransfersQuery } from "../../../store/services/affiliate/affiliate.service";
import { useAppSelector } from "../../../store";
import { getFullAffiliateState } from "../../../store/slices/affiliate/affiliate.slice";

const PER_PAGE = 20;

const columns: IColumn<AffiliateTransfer>[] = [
	{
		label: "Reference",
		dataKey: "reference",
		Cell: ({ rowData }) => (
			<Typography fontSize={13} fontWeight={600}>
				{rowData.reference}
			</Typography>
		)
	},
	{
		label: "Total Sales",
		dataKey: "totalSales",
		Cell: ({ rowData }) => <Typography fontSize={13}>{formatCurrency(rowData.totalSales, "EGP")}</Typography>
	},
	{
		label: "Amount Transferred",
		dataKey: "totalCommission",
		Cell: ({ rowData }) => (
			<Typography fontSize={13} fontWeight={600}>
				{formatCurrency(rowData.totalCommission, "EGP")}
			</Typography>
		)
	},
	{
		label: "Date",
		dataKey: "paidAt",
		Cell: ({ rowData }) => (
			<Typography fontSize={13}>{rowData.paidAt ? formatDate(rowData.paidAt, "Africa/Cairo", "PP") : "-"}</Typography>
		)
	}
];

const AffiliateTransfers = ({ payoutMethod }: { payoutMethod: PayoutMethod }) => {
	const theme = useTheme();
	const isSmallerScreen = useMediaQuery(theme.breakpoints.down("md"));
	const { affiliateTransfers, affiliateTransfersTotalItems, isFetchingAffiliateTransfers } =
		useAppSelector(getFullAffiliateState);
	const [FetchAffiliateTransfers] = useLazyGetAffiliateTransfersQuery();
	const [selectedTransfer, setSelectedTransfer] = useState<AffiliateTransfer | null>(null);
	const [page, setPage] = useState(1);

	useEffect(() => {
		FetchAffiliateTransfers({ page: 1, perPage: PER_PAGE });
	}, []);

	const handlePageChange = (newPage: number) => {
		if (newPage > 0) {
			setPage(newPage);
			FetchAffiliateTransfers({ page: newPage, perPage: PER_PAGE });
		}
	};

	return (
		<Box>
			<Card sx={{ p: 3, mb: 3 }}>
				<Typography variant={"subtitle1"} fontWeight={"bold"} mb={1.5}>
					Payout Details
				</Typography>
				<Box display={"flex"} flexDirection={"column"} gap={0.5} mt={1}>
					<Typography variant={"caption"} color={"textSecondary"}>
						Holder Name
					</Typography>
					<Typography fontWeight={600}>{payoutMethod.holderName}</Typography>
				</Box>
				{payoutMethod.type === IPayOutType.BANK ? (
					<Box display={"flex"} flexDirection={"column"} gap={0.5}>
						<Typography variant={"caption"} color={"textSecondary"}>
							Bank Name
						</Typography>
						<Typography fontWeight={600}>{payoutMethod.bankName}</Typography>
						<Typography variant={"caption"} color={"textSecondary"}>
							Account Number
						</Typography>
						<Typography fontWeight={600}>{payoutMethod.accountNumber}</Typography>
					</Box>
				) : (
					<Box display={"flex"} flexDirection={"column"} gap={0.5}>
						<Typography variant={"caption"} color={"textSecondary"}>
							Instapay / Mobile Wallet
						</Typography>
						<Typography fontWeight={600}>{payoutMethod.walletMobileNumber}</Typography>
					</Box>
				)}
			</Card>

			{isSmallerScreen ? (
				<AffiliateTransfersCardsList
					data={affiliateTransfers}
					onCardClick={setSelectedTransfer}
					page={page}
					perPage={PER_PAGE}
					totalItems={affiliateTransfersTotalItems}
					onPageChange={handlePageChange}
				/>
			) : (
				<MaterialTable
					tableLabel={"Transfers"}
					columns={columns}
					data={affiliateTransfers}
					isLoading={isFetchingAffiliateTransfers}
					onRowClick={setSelectedTransfer}
					page={page}
					rowsPerPage={PER_PAGE}
					totalItems={affiliateTransfersTotalItems}
					onPageChange={handlePageChange}
				/>
			)}

			<TransferBreakdownModal
				open={!!selectedTransfer}
				onClose={() => setSelectedTransfer(null)}
				transfer={selectedTransfer}
			/>
		</Box>
	);
};

export default AffiliateTransfers;
